import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from 'src/components/ui/button';
import { ProductsType } from 'src/constants/products';
import { PATH_ONBOARDING } from 'src/constants/spa-routes';
import { FormProvider, useForm } from 'react-hook-form';
import { formResolver } from 'src/utils/formResolver';
import { booleanScheme, stringScheme } from 'src/utils/validation-schemes';
import { useAppSelector } from 'src/store';
import {
  useGetConfigurationSettingsPromptQuery,
  useGetDefaultConfigurationTemplateFieldsQuery,
  useUpdateConfigurationSettingsPromptMutation,
} from 'src/services';
import { useLoader } from 'src/provider/LoaderProvider';
import * as yup from 'yup';
import { ChannelAccordion } from 'src/pages/OnboardingPage/OnboardingSteps/OnboardingStep5/ChannelAccordion/ChannelAccordion';
import { StyleAccordion } from 'src/pages/OnboardingPage/OnboardingSteps/OnboardingStep5/StyleAccordion/StyleAccordion';
import { ResponseLengthAccordion } from 'src/pages/OnboardingPage/OnboardingSteps/OnboardingStep5/ResponseLengthAccordion/ResponseLengthAccordion';
import type {
  ConfigurationFormFieldsObject,
  IndexedFormField,
} from 'src/pages/ConfigurationPage/hooks/useConfigurationFormFields';
import {
  toFormFieldsObject,
  withFieldIndex,
} from 'src/pages/ConfigurationPage/hooks/useConfigurationFormFields';
import type { ToneOfVoiceFormValues } from 'src/pages/ConfigurationPage/utils/getPersonaFormValues';
import { usePagedConfigurationSettings } from 'src/pages/ConfigurationPage/hooks/usePagedConfigurationSettings';

const OnboardingStep5 = () => {
  const [activeSettings, setActiveSettings] = useState(null);
  const [inputData, setInputData] = useState(null);
  const [formFields, setFormFields] = useState<IndexedFormField[] | null>(null);
  const [accordionValue, setAccordionValue] = useState('');
  const [formFieldsObject, setFormFieldsObject] = useState<ConfigurationFormFieldsObject>({});
  const loader = useLoader();
  const { project } = useAppSelector((s) => s.onboardingSlice);
  const { data: defaultTemplateFields, isLoading: isLoadingDefaultTemplateFields } =
    useGetDefaultConfigurationTemplateFieldsQuery();
  const { settings, isLoadingAllConfigurationSettings } = usePagedConfigurationSettings(
    project.id,
    { skip: !(project.id && defaultTemplateFields) },
    [],
  );
  const [
    updateConfigurationSettings,
    { data: updateConfigurationSettingsData, isLoading: isLoadingUpdateConfigurationSettings },
  ] = useUpdateConfigurationSettingsPromptMutation();
  const { data: configurationSettingsPrompt, isLoading: isLoadingConfigurationSettingsPrompt } =
    useGetConfigurationSettingsPromptQuery(
      { project_id: project.id, settings_id: activeSettings?.id },
      { skip: !(project.id && activeSettings && defaultTemplateFields) },
    );

  const navigate = useNavigate();

  useEffect(() => {
    if (formFields) {
      const fieldsObject = toFormFieldsObject(formFields);

      setFormFieldsObject(fieldsObject);
    }
  }, [formFields]);

  useEffect(() => {
    if (defaultTemplateFields) {
      if (!activeSettings && settings.length) {
        setActiveSettings(settings[0]);
      }

      if (activeSettings) {
        const newActiveSettings = settings.find((item) => {
          return item.id === activeSettings.id;
        });

        setActiveSettings(newActiveSettings);
      } else {
        const fields = withFieldIndex(defaultTemplateFields.form_fields);
        setFormFields(fields);
        setInputData(null);
      }
    }
  }, [settings, defaultTemplateFields]);

  useEffect(() => {
    if (configurationSettingsPrompt) {
      const fields = withFieldIndex(configurationSettingsPrompt.form_fields);

      setFormFields(fields);
      setInputData(configurationSettingsPrompt.input_data);
    }
  }, [configurationSettingsPrompt]);

  useEffect(() => {
    if (
      isLoadingDefaultTemplateFields ||
      isLoadingAllConfigurationSettings ||
      isLoadingConfigurationSettingsPrompt ||
      isLoadingUpdateConfigurationSettings
    ) {
      loader.show();
    } else {
      loader.hide();
    }
  }, [
    isLoadingDefaultTemplateFields,
    isLoadingAllConfigurationSettings,
    isLoadingConfigurationSettingsPrompt,
    isLoadingUpdateConfigurationSettings,
  ]);

  useEffect(() => {
    if (inputData) {
      methods.reset({
        pf_tone_of_voice_channel:
          inputData?.pf_tone_of_voice_channel ||
          formFieldsObject?.pf_tone_of_voice_channel?.default_value?.value ||
          'chat',
        pf_tone_of_voice_style:
          inputData?.pf_tone_of_voice_style ||
          formFieldsObject?.pf_tone_of_voice_style?.default_value?.value ||
          'casual',
        pf_tone_of_voice_resp_len:
          inputData?.pf_tone_of_voice_resp_len ||
          formFieldsObject?.pf_tone_of_voice_resp_len?.default_value?.value ||
          'concise',
        pf_tone_of_voice_style_custom: inputData?.pf_tone_of_voice_style_custom || '',
        pf_tone_of_voice_emoji:
          inputData?.pf_tone_of_voice_emoji ||
          formFieldsObject?.pf_tone_of_voice_emoji?.default_value?.value ||
          false,
      });
    }
  }, [inputData]);

  useEffect(() => {
    if (updateConfigurationSettingsData) {
      next();
    }
  }, [updateConfigurationSettingsData]);

  const back = () => {
    navigate(`${PATH_ONBOARDING.ROOT}/4?type=${ProductsType.customer}`, { replace: true });
  };

  const next = () => {
    navigate(`${PATH_ONBOARDING.ROOT}/6?type=${ProductsType.customer}`, { replace: true });
  };

  const methods = useForm<ToneOfVoiceFormValues>({
    defaultValues: {
      pf_tone_of_voice_channel:
        inputData?.pf_tone_of_voice_channel ||
        formFieldsObject?.pf_tone_of_voice_channel?.default_value?.value ||
        'chat',
      pf_tone_of_voice_style:
        inputData?.pf_tone_of_voice_style ||
        formFieldsObject?.pf_tone_of_voice_style?.default_value?.value ||
        'casual',
      pf_tone_of_voice_resp_len:
        inputData?.pf_tone_of_voice_resp_len ||
        formFieldsObject?.pf_tone_of_voice_resp_len?.default_value?.value ||
        'concise',
      pf_tone_of_voice_style_custom: inputData?.pf_tone_of_voice_style_custom || '',
      pf_tone_of_voice_emoji:
        inputData?.pf_tone_of_voice_emoji !== undefined
          ? inputData?.pf_tone_of_voice_emoji
          : formFieldsObject?.pf_tone_of_voice_emoji?.default_value?.value || false,
    },
    resolver: formResolver(
      yup.lazy((values) => {
        const baseSchema = {
          pf_tone_of_voice_emoji: booleanScheme(),
          pf_tone_of_voice_channel: stringScheme({
            required: formFieldsObject?.pf_tone_of_voice_channel?.is_required || false,
          }).label('Tone of Voice Channel'),
          pf_tone_of_voice_style: stringScheme({
            required: formFieldsObject?.pf_tone_of_voice_style?.is_required || false,
          }).label('Tone of Voice Style'),
          pf_tone_of_voice_style_custom: stringScheme({
            required:
              formFieldsObject?.pf_tone_of_voice_style_custom?.is_required ||
              values.pf_tone_of_voice_style === 'custom',
          }).label('Tone of Voice Style - Custom'),
        };

        return yup.object(baseSchema);
      }),
    ),
  });

  const style = methods.watch('pf_tone_of_voice_style');

  const submit = (data: ToneOfVoiceFormValues) => {
    if (!Object.keys(methods.formState.dirtyFields).length) {
      return next();
    }

    if (activeSettings) {
      const body = {
        input_data: {
          ...inputData,
          ...data,
          pf_tone_of_voice_emoji: ['casual', 'custom'].includes(style)
            ? data.pf_tone_of_voice_emoji
            : false,
          pf_tone_of_voice_style_custom:
            style === 'custom' ? data.pf_tone_of_voice_style_custom : null,
        },
      };

      updateConfigurationSettings({ project_id: project.id, settings_id: activeSettings.id, body });
    }
  };

  return (
    <div className={'w-full max-w-[650px] h-[65%] max-h-[65%]'}>
      <div className={'flex flex-col gap-[5px] px-[50px] py-1 h-full items-center overflow-y-auto'}>
        <FormProvider {...methods}>
          <form
            id={'onboarding-step-4'}
            className={'flex flex-col gap-5 w-full min-w-[455px] max-w-[455px]'}
            onSubmit={methods.handleSubmit(submit)}
          >
            <ChannelAccordion
              accordionValue={accordionValue}
              setAccordionValue={setAccordionValue}
              options={formFieldsObject?.pf_tone_of_voice_channel?.options}
            />
            <StyleAccordion
              accordionValue={accordionValue}
              setAccordionValue={setAccordionValue}
              options={formFieldsObject?.pf_tone_of_voice_style?.options}
            />
            <ResponseLengthAccordion
              accordionValue={accordionValue}
              setAccordionValue={setAccordionValue}
              options={formFieldsObject?.pf_tone_of_voice_resp_len?.options}
            />
          </form>
        </FormProvider>
      </div>
      <div className={'mt-5 flex items-center justify-center gap-5 px-[50px]'}>
        <div
          className={
            'flex flex-col items-center justify-center gap-5 w-full min-w-[455px] max-w-[455px]'
          }
        >
          <Button form={'onboarding-step-4'} className="w-full py-6 font-semibold">
            <p>Continue</p>
          </Button>
          <button
            type="button"
            onClick={back}
            className={'text-[#3F60B3] text-xs font-medium cursor-pointer hover:underline'}
          >
            ← Back
          </button>
        </div>
      </div>
    </div>
  );
};

export default OnboardingStep5;
